import { FC, useState } from "react";
import { Link as RouterLink, useFetcher, useParams } from "react-router";
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonList,
  Link,
  Select,
  Text,
} from "@postenbring/hedwig-react";
import {
  CertificateRevocationRequest,
  RevocationReason,
  SELECTABLE_REVOCATION_REASONS,
} from "../infrastructure/api/api";
import Translated from "../infrastructure/translation/Translated";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import { useRootPath } from "../infrastructure/translation/useRootPath";
import styles from "./CertificatePage.module.css";
import { useCertificate } from "./certificatesRouteContext";
import { formatTimestamp } from "./formatTimestamp";
import { DetailList, DetailRow } from "../DetailList/DetailList";
import { BackLink } from "../BackLink/BackLink";
import { linkExists } from "../infrastructure/entrypoint/link";
import { statusOf } from "./certificateStatus";

export const CertificatePage: FC = () => {
  const { orgnr } = useParams<{ orgnr: string }>();
  const rootPath = useRootPath();
  const translate = useTranslate();
  const organizationPath = `${rootPath}/organization/${orgnr}`;
  const certificatesPath = organizationPath + "/certificates";

  const certificate = useCertificate();
  const fetcher = useFetcher<{
    revokeFailed?: boolean;
    unregisterFailed?: boolean;
  } | null>();

  const [confirming, setConfirming] = useState(false);
  const [revocationReason, setRevocationReason] = useState<RevocationReason>(
    SELECTABLE_REVOCATION_REASONS[0],
  );

  const submitting = fetcher.state !== "idle";
  const failed = Boolean(
    fetcher.data?.revokeFailed || fetcher.data?.unregisterFailed,
  );

  if (!certificate) {
    return (
      <div className={styles.container}>
        <Text variant="h1" as="h1" size="min">
          <Translated textKey={"certificate.details_header"} />
        </Text>
        <p>
          <Translated textKey={"certificate.not_found"} />
        </p>
        <Link asChild>
          <RouterLink to={certificatesPath}>
            <Translated textKey={"generic.to_overview"} />
          </RouterLink>
        </Link>
      </div>
    );
  }

  const { revoked, displayName, issuer, notAfter, unregisteredAt } =
    certificate.certificate;
  const canUnregister = linkExists("unregister", certificate._links);

  const { isActive, certStatus } = statusOf(certificate);

  const revokedSection =
    issuer !== "DIGIPOST" ? (
      <DetailRow
        label={<Translated textKey={"certificate.status_unregistered"} />}
      >
        <Translated
          textKey={
            unregisteredAt !== null
              ? "certificate.revoked_true"
              : "certificate.revoked_false"
          }
        />
      </DetailRow>
    ) : (
      <DetailRow label={<Translated textKey={"certificate.revoked"} />}>
        <Translated
          textKey={
            revoked ? "certificate.revoked_true" : "certificate.revoked_false"
          }
        />
      </DetailRow>
    );

  const texts = canUnregister
    ? ({
        action: "certificate.unregister",
        warning: "certificate.unregister_warning",
        confirm: "certificate.unregister_confirm",
        submitting: "certificate.unregistering",
        error: "certificate.unregister_error",
      } as const)
    : ({
        action: "certificate.revoke",
        warning: "certificate.revoke_warning",
        confirm: "certificate.revoke_confirm",
        submitting: "certificate.revoking",
        error: "certificate.revoke_error",
      } as const);

  const handleSubmit = () => {
    if (canUnregister) {
      fetcher.submit(null, { method: "delete" });
      return;
    }

    const body: CertificateRevocationRequest = { reason: revocationReason };
    fetcher.submit(body, { method: "post", encType: "application/json" });
  };

  return (
    <div className={styles.container}>
      <div className={styles.titleRow}>
        <Text variant="h1" as="h1" size="min">
          <Translated textKey={"certificate.details_header"} />
        </Text>
        <Badge data-color={isActive ? "success" : "error"}>
          <Translated textKey={`certificate.status_${certStatus}`} />
        </Badge>
      </div>

      <Box variant="white" className={styles.card}>
        <div className={styles.cardTop}>
          <BackLink to={`${organizationPath}/certificates`} />
        </div>

        <DetailList>
          <DetailRow label={<Translated textKey={"certificate.name"} />}>
            {displayName}
          </DetailRow>
          <DetailRow label={<Translated textKey={"certificate.dn"} />}>
            <span className={styles.breakAll}>
              {certificate.certificate.subjectDn}
            </span>
          </DetailRow>
          <DetailRow
            label={<Translated textKey={"certificate.serial_number"} />}
          >
            <span className={styles.breakAll}>
              {certificate.certificate.serialNumber}
            </span>
          </DetailRow>
          <DetailRow label={<Translated textKey={"certificate.orgnr"} />}>
            {certificate.certificate.orgnr}
          </DetailRow>
          <DetailRow label={<Translated textKey={"certificate.issuer"} />}>
            {translate(`certificate.issuer_${issuer}`)}
          </DetailRow>
          {revokedSection}
          <DetailRow
            label={<Translated textKey={"certificate.created_time"} />}
          >
            {formatTimestamp(certificate.certificate.createdTime)}
          </DetailRow>
          <DetailRow label={<Translated textKey={"certificate.not_after"} />}>
            {formatTimestamp(notAfter)}
          </DetailRow>
        </DetailList>

        {isActive &&
          (confirming ? (
            <div className={styles.revokeConfirmation}>
              <Alert data-color="warning" role="alert">
                <Alert.Description>
                  <Translated textKey={texts.warning} />
                </Alert.Description>
              </Alert>
              {!canUnregister && (
                <Select
                  label={translate("certificate.revoke_reason_label")}
                  value={revocationReason}
                  onChange={(e) =>
                    setRevocationReason(e.target.value as RevocationReason)
                  }
                >
                  {SELECTABLE_REVOCATION_REASONS.map((reason) => (
                    <option key={reason} value={reason}>
                      {translate(`certificate.revoke_reason_${reason}`)}
                    </option>
                  ))}
                </Select>
              )}
              <ButtonList>
                <Button
                  size="small"
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  <Translated
                    textKey={submitting ? texts.submitting : texts.confirm}
                  />
                </Button>
                <Button
                  size="small"
                  variant="secondary"
                  type="button"
                  onClick={() => setConfirming(false)}
                  disabled={submitting}
                >
                  <Translated textKey={"generic.cancel"} />
                </Button>
              </ButtonList>
              {failed && (
                <p className={styles.error}>
                  <Translated textKey={texts.error} />
                </p>
              )}
            </div>
          ) : (
            <div className={styles.actions}>
              <Button size="small" onClick={() => setConfirming(true)}>
                <Translated textKey={texts.action} />
              </Button>
            </div>
          ))}
      </Box>
    </div>
  );
};
